import { cn } from "../lib/utils";
import * as React from "react";
import * as ResizablePrimitive from "react-resizable-panels";
const ResizablePanelAnimationContext = React.createContext(null);
/**
 * A user-resizable split layout built on react-resizable-panels: wraps
 * ResizablePanel regions separated by draggable ResizableHandle dividers,
 * with a `direction` of `horizontal` or `vertical`. Use it for split views
 * the user should rebalance (sidebar plus main area, list/detail); groups can
 * be nested to create grids of resizable regions.
 * @summary Resizable split-panel layout group.
 */
const ResizablePanelGroup = ({ animateLayoutChanges = false, className, ...props }) => {
    const [isDragging, setIsDragging] = React.useState(false);
    return (React.createElement(ResizablePanelAnimationContext.Provider, { value: animateLayoutChanges ? { isDragging, setIsDragging } : null },
        React.createElement(ResizablePrimitive.PanelGroup, { className: cn("flex h-full w-full data-[panel-group-direction=vertical]:flex-col", className), ...props })));
};
/**
 * One resizable region inside a ResizablePanelGroup; size it with
 * `defaultSize` (a percentage within the group).
 * @summary Single region of a resizable group.
 */
const ResizablePanel = React.forwardRef(({ className, ...props }, ref) => {
    const animationContext = React.useContext(ResizablePanelAnimationContext);
    return (React.createElement(ResizablePrimitive.Panel, { ref: ref, className: cn(className, animationContext &&
            (animationContext.isDragging
                ? "transition-none"
                : "transition-[flex-grow] duration-300 ease-out-quint")), ...props }));
});
ResizablePanel.displayName = "ResizablePanel";
/**
 * The draggable divider between two ResizablePanel regions.
 * @summary Draggable divider between panels.
 */
const ResizableHandle = ({ withHandle, className, onDragging, ...props }) => {
    const animationContext = React.useContext(ResizablePanelAnimationContext);
    const handleDragging = (isDragging) => {
        animationContext === null || animationContext === void 0 ? void 0 : animationContext.setIsDragging(isDragging);
        onDragging === null || onDragging === void 0 ? void 0 : onDragging(isDragging);
    };
    return (React.createElement(ResizablePrimitive.PanelResizeHandle, { className: cn("relative flex w-px items-center justify-center", "after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2", "focus-visible:outline-hidden focus-visible:ring-1", "focus-visible:ring-ring", "focus-visible:ring-offset-1 data-[panel-group-direction=vertical]:h-px", "data-[panel-group-direction=vertical]:w-full", "data-[panel-group-direction=vertical]:after:left-0", "data-[panel-group-direction=vertical]:after:h-1", "data-[panel-group-direction=vertical]:after:w-full", "data-[panel-group-direction=vertical]:after:-translate-y-1/2", "data-[panel-group-direction=vertical]:after:translate-x-0", "[&[data-panel-group-direction=vertical]>div]:rotate-90", "bg-primary-100", className), onDragging: handleDragging, ...props }, withHandle && (React.createElement("div", { className: cn("absolute flex h-6 w-2 items-center justify-center rounded-2xl", "border border-border bg-background") },
        React.createElement("div", { className: "w-px" })))));
};
export { ResizableHandle, ResizablePanel, ResizablePanelGroup };
//# sourceMappingURL=Resizable.js.map